import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
  ViewChild,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';

export type ChatSourceId = 'facebook' | 'google' | 'sms' | 'webchat' | 'openai' | 'platform' | 'email' | 'instagram';

export interface ChatSource {
  id: ChatSourceId;
  sourceName: string;
  shortSourceName?: string;
  account?: string;
}

export interface SentMessage {
  messageText: string;
  sourceId?: ChatSourceId;
  sourceName?: string;
}

@Component({
  selector: 'glxy-chat-composer',
  templateUrl: './chat-composer.component.html',
  styleUrls: ['./chat-composer.component.scss'],
})
export class ChatComposerComponent implements OnInit, OnDestroy {
  @HostBinding('class') class = 'glxy-chat-composer';

  @HostBinding('class.hide-extended-actions')
  get limitedActions(): boolean {
    // controls the hiding or showing of the 'extended-actions' element.
    // It is hidden when the chat composer component's width becomes too small
    return !this.showExtendedActions;
  }

  /**
   * Optional. The available chat sources to be shown in the chat sources
   * menu. If there is only one, that will be auto selected. If none, no
   * chat source text or selector is shown.
   */
  @Input() chatSources: ChatSource[] = [];

  /**
   * The messages textarea's content. You can use this input
   * to read or modify the text, such as adding links and content
   * to it.
   */
  @Input() public messageText = '';

  /**
   * Optional. The placeholder text for the message textarea
   */
  @Input() public placeholder = 'Type here…';

  /**
   * Optional. Set the selected chat source from those available in the
   * `chatSources` input above. If none set, the component will default to
   * the first chatSource item
   */
  @Input() set selectedChatSourceId(value: ChatSourceId | undefined) {
    this.currentChatSourceId = value;
    if (this.initialized) {
      this.changeChatSourceId(value);
    }
  }

  /**
   * Whether to send the message if you hit the `enter` key on your keyboard
   */
  @Input() public enterKeySendsMessage? = true;

  /* Optional. Controls the min height of the text area in the composer */
  @Input() public textAreaMinRows? = 1;

  /* Optional. Controls the max height of the text area in the composer */
  @Input() public textAreaMaxRows? = 5;

  /* Optional. Will disable the text box and send button if processing */
  @Input() processing? = false;

  /* Optional. Will disable the text box and send button if disabled is true */
  @Input() disabled? = false;

  /* Optional. Will clear the message text area if set to false */
  @Input() clearMessageTextOnSend? = true;

  /* Optional. Will not send out a message when message text is empty if disabled is true */
  @Input() disableSendEmptyText? = true;

  /* Optional. Will set the max length of the text area if provided */
  @Input() maxlength?: number;

  /* Optional. Specifies whether the send button will use a custom color class or fallback to the galaxy theme*/
  @Input() customColor?: boolean = false;

  /**
   * Emitted when the user sends a message.
   */
  @Output() send: EventEmitter<SentMessage> = new EventEmitter();

  /**
   * Emitted when the user changes chat sources.
   */
  @Output() selectedChatSourceIdChange: EventEmitter<ChatSourceId> = new EventEmitter();

  @ViewChild('composerTextArea', { static: true })
  public composerTextArea!: ElementRef<HTMLTextAreaElement>;

  currentChatSourceId?: ChatSourceId;
  currentChatSource?: ChatSource;

  showDynamicContentMinWidthPx = 400;
  showFullSourceNameMinWidthPx = 500;

  initialized = false;

  // element resizing for mobile and small screens
  showExtendedActions = false;
  showFullSourceName = false;

  private resizeObserver!: ResizeObserver;
  private showDynamicContentStore = false;
  private showSourceNameStore = false;

  constructor(
    private elementRef: ElementRef,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver((entries) => {
        const entry = Array.isArray(entries) ? entries[0] : entries;
        this.toggleDynamicContent(entry.contentRect.width);
        this.toggleSourceName(entry.contentRect.width);
      });
      this.resizeObserver.observe(this.elementRef.nativeElement);
    }
  }

  ngOnInit(): void {
    const firstChatSource = this.chatSources?.[0];

    // if no current chat source set, then use first in array
    if (!this.currentChatSourceId && firstChatSource) {
      this.changeChatSourceId(firstChatSource.id);
    } else if (this.currentChatSourceId) {
      this.changeChatSourceId(this.currentChatSourceId);
    }
    this.initialized = true;
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  sendMessage(event?: Event): void {
    // dont send if processing
    if (this.processing) {
      return;
    }

    // when hitting the enter key, we must stop the textinput
    // from adding a new line
    if (event) {
      event.preventDefault();
    }

    // focus the editor after sending to keep mobile
    // keyboard visible.
    this.focusTextArea();

    const messageText = this.messageText?.trim() ?? '';

    // dont sent if meessage is blank
    if (this.disableSendEmptyText && !messageText) return;

    const messageToSend: SentMessage = {
      messageText,
      sourceId: this.currentChatSourceId,
      sourceName: this.currentChatSource?.sourceName,
    };
    this.send.emit(messageToSend);

    if (this.clearMessageTextOnSend) {
      // clear message after sending
      this.clearMessageText();
    }
  }

  chatSourceLabel(): string {
    let label = '';
    if (this.showFullSourceName && this.currentChatSource?.sourceName) {
      label += this.currentChatSource.sourceName;
    } else {
      label += this.currentChatSource?.shortSourceName ?? this.currentChatSource?.sourceName;
    }
    if (this.currentChatSource?.account) {
      label += ' ' + this.currentChatSource.account;
    }
    return label;
  }

  public clearMessageText(): void {
    this.messageText = '';
  }

  public focusTextArea(): void {
    this.composerTextArea.nativeElement.focus();
  }

  changeChatSourceId(sourceId: ChatSourceId | undefined, emitChanges = false): void {
    if (this.chatSources.length === 0 || !sourceId) return;

    const newSource = this.chatSources.find((obj) => {
      return obj.id === sourceId;
    });

    if (newSource) {
      this.currentChatSourceId = sourceId;
      this.currentChatSource = newSource;
    } else {
      // if no source is found with the id then use the first
      // source in the list
      this.currentChatSourceId = this.chatSources[0].id;
      this.currentChatSource = this.chatSources[0];
    }

    if (emitChanges) {
      this.selectedChatSourceIdChange.emit(sourceId);
    }
  }

  public insertDynamicContent(
    dynamicContent: string,
    position: 'before' | 'after' | 'curser' = 'curser',
    selectInsertedCurserText = false,
  ): void {
    const textArea = this.composerTextArea.nativeElement;

    // If there is no message text, then don't add new lines to output
    const spacing = this.messageText !== '' ? '\n\n' : '';

    const startPos = textArea.selectionStart;
    const endPos = textArea.selectionEnd ?? startPos;

    let newValue: string;

    if (position === 'before') {
      dynamicContent = dynamicContent + spacing;
      newValue = dynamicContent + this.messageText;
    } else if (position === 'after') {
      dynamicContent = spacing + dynamicContent;
      newValue = this.messageText + dynamicContent;
    } else {
      // position is `curser`
      const contentBefore = textArea.value.substr(0, startPos);
      const contentAfter = textArea.value.substr(endPos, textArea.value.length);

      // add space before and after dynamic content if needed
      if (contentBefore && !contentBefore.endsWith(' ')) {
        dynamicContent = ' ' + dynamicContent;
      }
      if (!contentAfter.startsWith(' ')) {
        dynamicContent = dynamicContent + ' ';
      }
      newValue = contentBefore + dynamicContent + contentAfter;
    }

    this.messageText = newValue;
    textArea.value = newValue;

    // Using window.setTimeout fix bug where input does not focus
    window.setTimeout(() => {
      if (position === 'curser') {
        if (selectInsertedCurserText) {
          textArea.setSelectionRange(startPos, dynamicContent.length + startPos);
        } else {
          const curserPosition = dynamicContent.length + startPos;
          textArea.setSelectionRange(curserPosition, curserPosition);
        }
      } else if (position === 'after') {
        textArea.setSelectionRange(textArea.value.length, textArea.value.length);
      } else if (position === 'before') {
        textArea.setSelectionRange(0, 0);
      }
      textArea.focus();
    }, 0);
  }

  // Hide and show content based on the width of the component

  toggleDynamicContent(width: number): void {
    const updatedValue = width > this.showDynamicContentMinWidthPx;

    if (updatedValue === this.showDynamicContentStore) return;

    this.showDynamicContentStore = updatedValue;
    // window.setTimeout to fix detectChanges not firing
    window.setTimeout(() => this.changeDetectorRef.detectChanges(), 0);
    this.showExtendedActions = updatedValue;
  }

  toggleSourceName(width: number): void {
    const updatedValue = width > this.showFullSourceNameMinWidthPx;
    if (updatedValue === this.showSourceNameStore) return;

    this.showSourceNameStore = updatedValue;
    // window.setTimeout to fix detectChanges not firing
    window.setTimeout(() => this.changeDetectorRef.detectChanges(), 0);
    this.showFullSourceName = updatedValue;
  }
}
